import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check, MessageCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { INDUSTRIES, PARTNER_CATEGORIES, NAV_MENUS, WA_NUMBER } from "../data/site";

const SOLUTIONS = NAV_MENUS[0].items;

const DEFAULT_PROCESS = [
  { step: "Understand", desc: "Sector context, compliance needs and site realities captured first." },
  { step: "Design", desc: "Solutions designed for your industry's workloads and regulations." },
  { step: "Implement", desc: "Deployed with minimal disruption to ongoing operations." },
  { step: "Integrate", desc: "New systems unified with your existing technology ecosystem." },
  { step: "Support", desc: "SLA-driven support tuned to your sector's uptime expectations." },
];

const HeroGlyph = ({ icon: Icon }) => (
  <div className="relative hidden lg:flex items-center justify-center h-72 w-72 shrink-0">
    <div className="absolute inset-0 rounded-full border border-dashed border-smipl-orange/25 animate-[spin_40s_linear_infinite]" />
    <div className="absolute inset-8 rounded-full border border-white/10 animate-[spin_28s_linear_infinite_reverse]" />
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="relative flex h-24 w-24 items-center justify-center rounded-3xl bg-smipl-orange/10 border border-smipl-orange/30 text-smipl-orange">
        <span className="animate-pulse-ring absolute inset-0 rounded-3xl border border-smipl-orange/40" />
        <Icon size={40} />
      </div>
    </div>
  </div>
);

const ConvergeVisual = () => {
  const nodes = [
    { label: "Network", x: 16, y: 14 }, { label: "Security", x: 50, y: 8 }, { label: "AV", x: 84, y: 14 },
    { label: "Compute", x: 16, y: 86 }, { label: "Cloud", x: 50, y: 92 }, { label: "Power", x: 84, y: 86 },
  ];
  return (
    <div className="relative h-80 w-full max-w-lg" data-testid="converge-visual">
      <svg className="absolute inset-0 h-full w-full">
        {nodes.map((n, i) => (
          <motion.line
            key={n.label}
            x1={`${n.x}%`} y1={`${n.y}%`} x2="50%" y2="50%"
            stroke="rgba(245,124,0,0.35)" strokeWidth="1"
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.2 + i * 0.15 }}
          />
        ))}
      </svg>
      {nodes.map((n, i) => (
        <motion.span
          key={n.label}
          initial={{ opacity: 0, scale: 0.6 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.12, duration: 0.5 }}
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/15 bg-navy-ink px-3.5 py-1.5 text-[11px] font-mono tracking-wider text-slate-300"
          style={{ left: `${n.x}%`, top: `${n.y}%` }}
        >
          {n.label}
        </motion.span>
      ))}
      <motion.span
        initial={{ opacity: 0, scale: 0.5 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-smipl-orange px-5 py-2.5 text-xs font-bold tracking-widest text-white glow-orange"
      >
        ONE ECOSYSTEM
      </motion.span>
    </div>
  );
};

const ProcessSteps = ({ steps, testId }) => {
  const [active, setActive] = useState(0);
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-3">
        {steps.map((s, i) => (
          <button
            key={s.step}
            data-testid={`${testId}-step-${i}`}
            onClick={() => setActive(i)}
            className={`group flex items-center gap-3 rounded-2xl border px-5 py-4 transition-all duration-300 ${
              active === i ? "border-smipl-orange/60 bg-smipl-orange/10" : "border-white/10 bg-white/[0.03] hover:border-white/25"
            }`}
          >
            <span className={`font-mono text-[11px] ${active === i ? "text-smipl-orange" : "text-slate-600"}`}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className={`font-display text-sm font-bold ${active === i ? "text-white" : "text-slate-400 group-hover:text-white"}`}>
              {s.step}
            </span>
          </button>
        ))}
      </div>
      <motion.p
        key={active}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-6 max-w-2xl rounded-2xl border border-white/8 bg-white/[0.03] p-6 text-sm md:text-base text-slate-300 leading-relaxed"
      >
        {steps[active].desc}
      </motion.p>
    </div>
  );
};

const BlockTitle = ({ children }) => (
  <Reveal>
    <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-8">{children}</h2>
  </Reveal>
);

const ServiceDetail = ({ detail, item, group, groupLabel, openContact }) => {
  const industries = INDUSTRIES.filter((i) => detail.industries?.includes(i.id));
  const ecosystem = PARTNER_CATEGORIES.filter((p) => detail.ecosystem?.includes(p.id));
  const solutions = SOLUTIONS.filter((s) => detail.solutions?.includes(s.id));
  const steps = detail.process || DEFAULT_PROCESS;

  return (
    <main className="pt-36 pb-28 min-h-screen" data-testid={`detail-page-${item.id}`}>
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <Link
            to={`/${group}`}
            data-testid="detail-back-link"
            className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-slate-500 hover:text-smipl-orange transition-colors"
          >
            <ArrowLeft size={13} /> {groupLabel}
          </Link>
        </Reveal>

        <div className={`mt-10 ${detail.converge ? "" : "flex items-center justify-between gap-10"}`}>
          <div className="max-w-2xl">
            <Reveal delay={0.05}>
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-smipl-orange">{groupLabel} / {item.name}</p>
            </Reveal>
            <Reveal delay={0.12}>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="detail-hero-title">
                {detail.heroTitle}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base md:text-lg text-slate-400 leading-relaxed">{detail.heroSub}</p>
            </Reveal>
          </div>
          {detail.converge ? (
            <Reveal delay={0.25} className="mt-10 flex justify-center"><ConvergeVisual /></Reveal>
          ) : (
            <Reveal delay={0.25}><HeroGlyph icon={item.icon} /></Reveal>
          )}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-16 max-w-3xl text-base md:text-lg text-slate-300 leading-relaxed border-l-2 border-smipl-orange pl-6">
            {detail.overview}
          </p>
        </Reveal>

        <section className="mt-24" data-testid="detail-capabilities">
          <BlockTitle>Core Capabilities</BlockTitle>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {detail.capabilities.map((c, i) => (
              <Reveal key={c} delay={(i % 3) * 0.06}>
                <div className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.03] px-5 py-4 transition-all duration-300 hover:border-smipl-orange/40 hover:-translate-y-0.5">
                  <Check size={15} className="shrink-0 text-smipl-orange" />
                  <span className="text-sm text-slate-200">{c}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="mt-24" data-testid="detail-process">
          <BlockTitle>How We Deliver</BlockTitle>
          <Reveal delay={0.1}>
            <ProcessSteps steps={steps} testId="detail-process-steps" />
          </Reveal>
        </section>

        <section className="mt-24" data-testid="detail-deliver">
          <BlockTitle>{detail.deliverLabel}</BlockTitle>
          <div className="flex flex-wrap gap-3">
            {detail.deliver.map((d) => (
              <span key={d} className="rounded-full border border-white/12 bg-white/[0.04] px-5 py-2.5 text-sm text-slate-200">
                {d}
              </span>
            ))}
          </div>
        </section>

        {solutions.length > 0 && (
          <section className="mt-24" data-testid="detail-solutions">
            <BlockTitle>Relevant Solutions</BlockTitle>
            <div className="flex flex-wrap gap-3">
              {solutions.map((s) => (
                <Link
                  key={s.id}
                  to={`/solutions/${s.id}`}
                  data-testid={`detail-solution-${s.id}`}
                  className="group flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-5 py-2.5 text-sm text-slate-200 hover:border-smipl-orange/50 transition-colors"
                >
                  <s.icon size={15} className="text-smipl-orange" /> {s.name}
                  <ArrowUpRight size={13} className="text-smipl-orange opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {ecosystem.length > 0 && (
          <section className="mt-24" data-testid="detail-ecosystem">
            <BlockTitle>Technology Ecosystem</BlockTitle>
            <div className="flex flex-wrap gap-3">
              {ecosystem.map((p) => (
                <Link
                  key={p.id}
                  to={`/partners/${p.id}`}
                  data-testid={`detail-eco-${p.id}`}
                  className="group flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-5 py-2.5 text-sm text-slate-200 hover:border-smipl-orange/50 transition-colors"
                >
                  <p.icon size={15} className="text-smipl-orange" /> {p.name}
                  <ArrowUpRight size={13} className="text-smipl-orange opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {industries.length > 0 && (
          <section className="mt-24" data-testid="detail-industries">
            <BlockTitle>Relevant Industries</BlockTitle>
            <div className="flex flex-wrap gap-3">
              {industries.map((ind) => (
                <Link
                  key={ind.id}
                  to={`/industries/${ind.id}`}
                  data-testid={`detail-industry-${ind.id}`}
                  className="group flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-5 py-2.5 text-sm text-slate-200 hover:border-smipl-orange/50 transition-colors"
                >
                  <ind.icon size={15} className="text-smipl-orange" /> {ind.name}
                  <ArrowUpRight size={13} className="text-smipl-orange opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-24" data-testid="detail-kpi">
          <BlockTitle>Why SMIPL for {item.name}</BlockTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            {detail.kpi.map((k, i) => (
              <Reveal key={k} delay={(i % 2) * 0.07}>
                <div className="tracing-border rounded-2xl border border-white/8 bg-white/[0.03] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-smipl-orange/40">
                  <span className="font-mono text-[11px] tracking-[0.25em] text-smipl-orange">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-2 text-sm md:text-base font-medium text-slate-200">{k}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <Reveal delay={0.1}>
          <div className="mt-24 rounded-3xl border border-smipl-orange/25 bg-gradient-to-br from-smipl-orange/10 to-transparent p-10 md:p-14 text-center" data-testid="detail-cta">
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{detail.cta}</h2>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <button
                data-testid="detail-talk-btn"
                onClick={openContact}
                className="flex items-center gap-2 rounded-full bg-smipl-orange text-white px-8 py-3.5 text-sm font-semibold hover:bg-smipl-amber hover:glow-orange transition-all duration-300"
              >
                Let's Talk <ArrowUpRight size={15} />
              </button>
              <button
                data-testid="detail-whatsapp-btn"
                onClick={() => (WA_NUMBER ? window.open(`https://wa.me/${WA_NUMBER}`, "_blank") : openContact())}
                className="flex items-center gap-2 rounded-full border border-wa/50 text-wa px-8 py-3.5 text-sm font-semibold hover:bg-wa hover:text-navy-deep transition-all duration-300"
              >
                <MessageCircle size={15} /> WhatsApp
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default ServiceDetail;
